/**
 * workshopHooks.js  —  FRONTEND
 * ─────────────────────────────────────────────────────────────
 * Location: src/Pages/Customers/WorkshopDashboard/workshopHooks.js
 *
 * React Query hooks for the Workshop module.
 * Import these in components — never call workshopApi.js directly.
 *
 * Setup (once in index.js or App.js):
 *   import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
 *   const queryClient = new QueryClient();
 *   <QueryClientProvider client={queryClient}><App /></QueryClientProvider>
 *
 * Install:
 *   npm install @tanstack/react-query axios
 * ─────────────────────────────────────────────────────────────
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAllCases,
  getCaseById,
  getCaseStatus,
  submitCaseDetails,
  uploadDocument,
  submitSignedClaimForm,
  saveOnSpotService,
  getSurveyorDetail,
  getWorkshopDetail,
  getDismantleDetail,
  getDocuments,
  getCaseByAccidentNo,
} from "./workshopApi";

// ─── Query key constants ──────────────────────────────────────
// Used to identify cached data and invalidate it when needed.
export const QUERY_KEYS = {
  cases:      ["workshop", "cases"],
  case:       (id) => ["workshop", "cases", id],
  caseStatus: (id) => ["workshop", "cases", id, "status"],
};

// ═══════════════════════════════════════════════════════════════
// 1. useAllCases
//    API: GET /api/customer/cases
//    Controller: getCustomerCases
//
//    Fires automatically on mount. Populates the cases table.
//    Backend filters by req.user.phone so only the logged-in
//    customer's cases are returned.
//
//    data = [ { id, accidentCaseNo, vehicleNumber,
//               stage, currentStatus, createdAt }, ... ]
// ═══════════════════════════════════════════════════════════════
export const useAllCases = () => {
  return useQuery({
    queryKey: QUERY_KEYS.cases,
    queryFn:  getAllCases,
    staleTime: 30_00000, // treat data as fresh for 30s before background refetch
  });
};

// ═══════════════════════════════════════════════════════════════
// 2. useSearchCase
//    API: GET /api/customer/cases/:caseId
//    Controller: getCustomerCaseById
//
//    Does NOT fire automatically (enabled: false).
//    Only fires when you call refetch() — i.e. user clicks Search.
//    Returns 404 from backend if caseId doesn't exist →
//    React Query sets isError: true so you can show "Case not found".
//
//    Usage:
//      const { refetch, isFetching, isError } = useSearchCase(query);
//      <button onClick={() => refetch()}>Search</button>
// ═══════════════════════════════════════════════════════════════
export const useSearchCase = (caseId) => {
  return useQuery({
    queryKey: QUERY_KEYS.case(caseId),
    queryFn:  () => getCaseById(caseId),
    enabled:  false,  // manual trigger only
    retry:    false,  // don't retry on 404 — show error immediately
  });
};

// ═══════════════════════════════════════════════════════════════
// useGlobalCaseSearch
// Manual React Query hook for the public global-search endpoint.
// Returns data = { case, details, documents, timeline }
export const useGlobalCaseSearch = (accidentCaseNo) => {
  return useQuery({
    queryKey: ["workshop", "globalSearch", accidentCaseNo],
    queryFn:  () => getCaseByAccidentNo(accidentCaseNo),
    enabled:  false,
    retry:    false,
  });
};

// ═══════════════════════════════════════════════════════════════
// 3. useSubmitCase
//    API: POST /api/customer/cases/:caseId/details
//    Controller: submitCustomerDetails
//
//    Sends all inspection form text fields to backend.
//    On success → invalidates useAllCases so table auto-refreshes.
//
//    The details object must match backend req.body fields exactly:
//      driverName, driverNumber, helperPresent, helperName,
//      helperNumber, rootCause, isLoaded, thirdPartyLoss,
//      firNumber, claimNumber, insuranceCompany,
//      companyRepresentativeName, companyRepresentativeEmail,
//      companyRepresentativePhone, isAdvocateNeeded,
//      powerOfAttorneyHolderName, powerOfAttorneyHolderAadhar,
//      firOrGdDate, vehicleReleaseDate, jamantiDate
//
//    Usage:
//      const { mutateAsync, isPending } = useSubmitCase();
//      await mutateAsync({ caseId, details });
// ═══════════════════════════════════════════════════════════════
export const useSubmitCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: submitCaseDetails, // receives { caseId, details }
    onSuccess: () => {
      // Table will auto-refetch after form submit
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.cases });
    },
  });
};

// ═══════════════════════════════════════════════════════════════
// 4. useUploadDocuments

export const useUploadDocuments = () => {
  return useMutation({
    mutationFn: uploadDocument, // receives { caseId, documentType, file }
  });
};

// ═══════════════════════════════════════════════════════════════
// 6. useSubmitSignedClaimForm
//    Uploads the signed PDF for a case.
//    On success, invalidates the case query so caseData refreshes.
//
//    Usage:
//      const { mutateAsync, isPending } = useSubmitSignedClaimForm();
//      await mutateAsync({ caseId, file });

export const useSubmitSignedClaimForm = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: submitSignedClaimForm, // receives { caseId, file }
    onSuccess: (_, { caseId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.case(caseId) });
    },
  });
};

// ═══════════════════════════════════════════════════════════════
// 5. useCaseStatus
//    API: GET /api/customer/cases/:caseId/status
//    Controller: getCustomerCaseStatus
//
//    Fires on mount when caseId is available.
//    Auto-refetches every 60s for live repair stage updates.
//
//    Backend returns:
//      { success: true, data: { accidentCaseNo, vehicleNumber,
//                               stage, currentStatus } }
//
//    stage / currentStatus are your DB fields that track
//    where the repair is in the pipeline.
//
//    Usage in CustomerWorkshopView:
//      const { data, isLoading, isError } = useCaseStatus(caseData.id);
//      const stage = data?.data?.stage;
//      const status = data?.data?.currentStatus;
// ═══════════════════════════════════════════════════════════════
export const useCaseStatus = (caseId) => {
  return useQuery({
    queryKey:        QUERY_KEYS.caseStatus(caseId),
    queryFn:         () => getCaseStatus(caseId),
    enabled:         !!caseId,
    staleTime:       60_000,
    refetchInterval: 60_000, // poll every 60s — keeps stage live
  });
};

export const useSaveOnSpotService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: saveOnSpotService, // receives { caseId, onSpotService }
    onSuccess: (_, { caseId }) => {
      // ✅ invalidate caseStatus so the step reflects saved service
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.caseStatus(caseId) });
    },
  });
};

export const useDocuments = (caseId, enabled = false) => {
  return useQuery({
    queryKey: ["workshop", "cases", caseId, "documents"],
    queryFn:  () => getDocuments(caseId),
    enabled:  !!caseId && enabled,
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: false,
  });
};

export const useSurveyorDetail = (caseId, enabled = false) => {
  return useQuery({
    queryKey: ["workshop", "cases", caseId, "surveyor"],
    queryFn:  () => getSurveyorDetail(caseId),
    enabled:  !!caseId && enabled,
    staleTime: 60_000,
    refetchInterval: 60_000, 
    retry: false,
  });
};

export const useWorkshopDetail = (caseId, enabled = false) => {
  return useQuery({
    queryKey: ["workshop", "cases", caseId, "workshop-detail"],
    queryFn:  () => getWorkshopDetail(caseId),
    enabled:  !!caseId && enabled,
    staleTime: 60_000,
    refetchInterval: 60_000, 
    retry: false,
  });
};

export const useDismantleDetail = (caseId, enabled = false) => {
  return useQuery({
    queryKey: ["workshop", "cases", caseId, "dismantle-detail"],
    queryFn:  () => getDismantleDetail(caseId),
    enabled:  !!caseId && enabled,
    staleTime: 60_000,
    refetchInterval: 60_000, 
    retry: false,
  });
};